type TOrderMetricItem = { dataSource: string; metric: string };

const toOrderMetricKey = (dataSource: string, metric: string): string =>
  `${dataSource}::${metric}`;

type TCascadeOptionsChildItem = {
  value?: string;
};

type TCascadeOptionsItem = {
  value?: string;
  children?: TCascadeOptionsChildItem[];
};

/** 按页面 MetricSelect 勾选的 metric value 顺序生成 order_metrics */
export const buildOrderMetricsFromPageMetricValues = (
  metricValues: string[] = [],
  sourceMetrics: Record<string, string[]> = {},
): TOrderMetricItem[] => {
  if (!metricValues.length) return [];

  const result: TOrderMetricItem[] = [];
  const seen = new Set<string>();

  metricValues.forEach(metric => {
    const dataSource = Object.keys(sourceMetrics || {}).find(key =>
      sourceMetrics[key]?.includes(metric),
    );
    if (!dataSource) return;
    const key = toOrderMetricKey(dataSource, metric);
    if (seen.has(key)) return;
    result.push({ dataSource, metric });
    seen.add(key);
  });

  return result;
};

/** 按下载弹窗 cascader 勾选顺序生成 order_metrics（与 Steam Data tab 一致） */
export const buildOrderMetricsFromDetailSelectorValues = (
  selectorValues: string[] = [],
): TOrderMetricItem[] => {
  const normalizeDetailOrderDataSource = (
    metric: string,
    uiDataSource?: string,
  ): string => {
    if (metric.startsWith('gamalytic__')) return 'gamalytic';
    if (metric.startsWith('source_steam__')) return 'source_steam';
    if (metric.startsWith('alinea__')) return 'alinea';
    if (metric === 'steamfollowers' || metric === 'steamreviews') {
      return 'vginsights';
    }
    if (metric === 'pcu' || metric === 'acu') return 'steamcharts';
    return uiDataSource || '';
  };

  return selectorValues
    .map(selectorValue => {
      const [uiDataSource, metric] = String(selectorValue).split('###');
      if (!metric) return null;
      return {
        metric,
        dataSource: normalizeDetailOrderDataSource(metric, uiDataSource),
      };
    })
    .filter((item): item is TOrderMetricItem => Boolean(item));
};

/** 将下载弹窗 metrics 转为 cascader 选项值（dataSource###metric） */
export const toDetailMetricSelectorValues = (
  metrics: Array<string | string[]> = [],
): string[] =>
  metrics
    .map(item => {
      if (typeof item === 'string') return item;
      if (Array.isArray(item) && item.length >= 2) {
        return `${item[0]}###${item[1]}`;
      }
      return '';
    })
    .filter(Boolean);

/** 按下载弹窗 cascade 选项顺序，将 source_metrics 展开为 order_metrics */
export const buildOrderMetricsFromSourceMetrics = (
  sourceMetrics: Record<string, string[]> = {},
  cascadeOptions: TCascadeOptionsItem[] = [],
): TOrderMetricItem[] => {
  const optionOrder: string[] = [];
  cascadeOptions.forEach(group => {
    group.children?.forEach(child => {
      const metric = child.value?.split('###')?.[1];
      if (metric) optionOrder.push(metric);
    });
  });

  const result: TOrderMetricItem[] = [];
  const seen = new Set<string>();

  optionOrder.forEach(metric => {
    const dataSource = Object.keys(sourceMetrics).find(key =>
      sourceMetrics[key]?.includes(metric),
    );
    if (!dataSource) return;
    const key = toOrderMetricKey(dataSource, metric);
    if (seen.has(key)) return;
    result.push({ dataSource, metric });
    seen.add(key);
  });

  Object.entries(sourceMetrics).forEach(([dataSource, metrics]) => {
    metrics?.forEach(metric => {
      const key = toOrderMetricKey(dataSource, metric);
      if (seen.has(key)) return;
      result.push({ dataSource, metric });
      seen.add(key);
    });
  });

  return result;
};

/** mergeData / Summary 用：按 order_metrics 顺序展开 source_metrics */
export const getOrderedMetricEntriesFromParams = (
  params: Record<string, any> = {},
): TOrderMetricItem[] => {
  const sourceMetrics = params.source_metrics || {};
  const orderMetrics = params.order_metrics || [];

  if (!orderMetrics.length) {
    const fallback: TOrderMetricItem[] = [];
    Object.keys(sourceMetrics).forEach(dataSource => {
      sourceMetrics[dataSource]?.forEach((metric: string) => {
        fallback.push({ dataSource, metric });
      });
    });
    return fallback;
  }

  const result: TOrderMetricItem[] = [];
  const seen = new Set<string>();

  orderMetrics.forEach(({ dataSource, metric }: TOrderMetricItem) => {
    const key = toOrderMetricKey(dataSource, metric);
    if (sourceMetrics[dataSource]?.includes(metric) && !seen.has(key)) {
      result.push({ dataSource, metric });
      seen.add(key);
    }
  });

  Object.entries(sourceMetrics).forEach(([dataSource, metrics]) => {
    (metrics as string[])?.forEach(metric => {
      const key = toOrderMetricKey(dataSource, metric);
      if (!seen.has(key)) {
        result.push({ dataSource, metric });
        seen.add(key);
      }
    });
  });

  return result;
};
